import React, { useEffect, useMemo, useState } from 'react';
import { ESTILOS, ASPECTOS, estiloPorClave } from '../ai/estilos';
import { generarImagen, consultarEstado, cancelarGeneracion, listarGeneraciones, eliminarGeneracion, guardarComoFoto } from '../services/iaImagenService';
import { useToast } from '../context/ToastContext';
import { mensajeAmigable } from '../utils/errores';
import CreditosIABadge from './CreditosIABadge';
import { optimizarFoto } from '../utils/imagen';

// "Crear una fotografía profesional" a partir de una foto del producto.
// El usuario no necesita entender de IA: sube su foto, elige un estilo y listo.
export default function MejorarConIAModal({ producto, onCerrar, onActualizado }) {
  const { mostrarToast } = useToast();

  const fotos = useMemo(
    () => [1, 2, 3, 4, 5].map((n) => ({ n, url: producto[`foto_${n}_url`] })).filter((f) => f.url),
    [producto]
  );
  const [origen, setOrigen] = useState(() => producto.foto_principal && producto[`foto_${producto.foto_principal}_url`] ? producto.foto_principal : (fotos[0]?.n || 1));
  const [estilo, setEstilo] = useState(null);
  const [instrucciones, setInstrucciones] = useState('');
  const [aspecto, setAspecto] = useState('1:1');
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(null);
  const [reemplazando, setReemplazando] = useState(null); // { comoPrincipal } cuando hay que elegir cuál foto reemplazar
  const [refrescarCreditos, setRefrescarCreditos] = useState(0);

  const urlOrigen = producto[`foto_${origen}_url`];
  const esPersonalizada = estilo === 'personalizada';

  const cargarHistorial = async () => {
    try {
      const lista = await listarGeneraciones(producto.id);
      setHistorial(lista);
      // Fotos que quedaron en proceso (la IA tardó): se consulta al servidor para
      // que las entregue si el proveedor ya terminó, y se recarga el historial.
      const pendientes = lista.filter((g) => g.estado === 'processing');
      if (pendientes.length) {
        await Promise.all(pendientes.map((g) => consultarEstado(g.id).catch(() => null)));
        setHistorial(await listarGeneraciones(producto.id));
      }
    } catch { /* silencio */ }
  };
  useEffect(() => {
    cargarHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producto.id]);

  const elegirEstilo = (clave) => {
    setEstilo(clave);
    const e = estiloPorClave(clave);
    if (e) setAspecto(e.aspecto);
  };

  const puedeGenerar = !!estilo && !!urlOrigen && !generando && (!esPersonalizada || instrucciones.trim().length > 0);

  const generar = async () => {
    if (!puedeGenerar) {
      if (esPersonalizada && !instrucciones.trim()) mostrarToast('Escribe cómo quieres la foto.', 'error');
      return;
    }
    setGenerando(true); setResultado(null);
    try {
      const gen = await generarImagen({
        productoId: producto.id, imagenOrigenIdx: origen, estilo,
        instrucciones: instrucciones.trim() || null, aspecto,
      });
      // Rápido (mock/gemini): ya viene completa. Lento (Kie): viene 'processing'
      // y esperamos consultando el estado (sin que se corte por tiempo).
      const listo = (gen && gen.estado === 'completed' && gen.resultado_url) ? gen : await esperarResultado(gen && gen.id);
      if (listo && listo.estado === 'completed' && listo.resultado_url) {
        setResultado(listo);
        mostrarToast('¡Foto lista! Mira cómo quedó 👇', 'exito');
      } else {
        mostrarToast(listo?.error_mensaje || 'No se pudo generar la imagen. Prueba de nuevo.', 'error', 6000);
      }
      cargarHistorial();
    } catch (e) {
      // Los errores del servidor de IA ya traen un mensaje claro (e.code + e.message).
      mostrarToast(e.code ? e.message : mensajeAmigable(e), 'error', 6000);
    } finally {
      setGenerando(false);
      setRefrescarCreditos((x) => x + 1); // actualiza el contador de fotos disponibles
    }
  };

  // Espera el resultado de una generación asíncrona consultando cada 5s (~3 min máx).
  const esperarResultado = async (id) => {
    if (!id) return { estado: 'failed', error_mensaje: 'No se pudo iniciar la generación.' };
    for (let i = 0; i < 48; i++) { // ~4 min: da margen cuando el proveedor está lento
      await new Promise((r) => setTimeout(r, 5000));
      let d;
      try { d = await consultarEstado(id); } catch { continue; }
      const g = d && d.generacion;
      if (g && g.estado === 'completed' && g.resultado_url) return g;
      if ((g && g.estado === 'failed') || (d && d.error && !d.procesando)) {
        return { estado: 'failed', error_mensaje: (d && d.mensaje) || (g && g.error_mensaje) || 'No se pudo generar la imagen.' };
      }
    }
    // Se agotó la espera: la foto sigue en proceso en el servidor (no se
    // reembolsa mientras el proveedor trabaja) y aparecerá en el historial.
    const c = await cancelarGeneracion(id);
    return { estado: 'failed', error_mensaje: (c && c.mensaje) || 'Tu foto está tardando más de lo normal. Sigue en proceso y aparecerá en el historial del producto cuando esté lista.' };
  };

  const guardar = async (comoPrincipal) => {
    if (!resultado?.resultado_url) return;
    setGuardando(true);
    try {
      const r = await guardarComoFoto(producto, resultado.resultado_url, { comoPrincipal });
      if (!r.ok && r.motivo === 'SIN_ESPACIO') {
        // Las 5 fotos están llenas → que elija cuál reemplazar (no se borra sola ninguna).
        setReemplazando({ comoPrincipal });
        return;
      }
      const cambios = { [`foto_${r.slot}_url`]: resultado.resultado_url };
      if (comoPrincipal) cambios.foto_principal = r.slot;
      mostrarToast(comoPrincipal ? 'Guardada y puesta como principal ⭐' : 'Guardada como foto del producto ✅', 'exito');
      onActualizado && onActualizado(cambios);
      setResultado(null);
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Reemplaza la foto del espacio elegido por la generada.
  const reemplazarEn = async (slot) => {
    if (!resultado?.resultado_url) return;
    setGuardando(true);
    try {
      await guardarComoFoto(producto, resultado.resultado_url, { comoPrincipal: reemplazando?.comoPrincipal, slot });
      const cambios = { [`foto_${slot}_url`]: resultado.resultado_url };
      if (reemplazando?.comoPrincipal) cambios.foto_principal = slot;
      mostrarToast(reemplazando?.comoPrincipal ? 'Reemplazada y puesta como principal ⭐' : 'Foto reemplazada ✅', 'exito');
      onActualizado && onActualizado(cambios);
      setReemplazando(null);
      setResultado(null);
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Descarga la imagen generada al dispositivo (para redes, WhatsApp, etc.).
  const descargar = async () => {
    if (!resultado?.resultado_url) return;
    const nombre = `${(producto.nombre || 'producto').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-ia.png`;
    try {
      const resp = await fetch(resultado.resultado_url);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = nombre;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      mostrarToast('Descargando la imagen 📥', 'exito');
    } catch {
      // Respaldo (móvil): abrir la imagen para guardarla manteniendo presionado.
      window.open(resultado.resultado_url, '_blank', 'noopener,noreferrer');
      mostrarToast('Se abrió la imagen. Mantené presionado para guardarla en tu galería.', 'info', 6000);
    }
  };

  const borrar = async (gen) => {
    try {
      await eliminarGeneracion(gen);
      setConfirmarBorrar(null);
      if (resultado?.id === gen.id) setResultado(null);
      cargarHistorial();
      mostrarToast('Generación eliminada 🗑️', 'exito');
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const P = 'var(--color-primario,#DB2777)';
  const card = { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '620px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' };
  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '14px' };
  const seccion = { padding: '16px 20px', borderBottom: '1px solid #F3F4F6' };

  const completadas = historial.filter((g) => g.estado === 'completed' && g.resultado_url);

  return (
    <div style={overlay} onClick={onCerrar}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ ...seccion, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '19px', color: P }}>✨ Crear foto profesional</h2>
            <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: '#9CA3AF' }}>Tu foto original siempre se conserva.</p>
            <div><CreditosIABadge refrescar={refrescarCreditos} /></div>
          </div>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>

        {/* Elegir foto de referencia (si hay más de una) */}
        {fotos.length > 1 && !resultado && (
          <div style={seccion}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>1. ¿Cuál foto quieres mejorar?</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {fotos.map((f) => (
                <button key={f.n} onClick={() => setOrigen(f.n)}
                  style={{ padding: 0, border: origen === f.n ? `3px solid ${P}` : '2px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', background: '#fff', width: '72px', height: '72px' }}>
                  <img src={optimizarFoto(f.url, 160)} alt={`Foto ${f.n}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Resultado / vista previa antes-después */}
        {resultado && resultado.resultado_url && (
          <div style={seccion}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Comparación</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Figura etiqueta="Antes" url={urlOrigen} />
              <Figura etiqueta="Después" url={resultado.resultado_url} destacado P={P} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '12px' }}>
              <button onClick={() => guardar(false)} disabled={guardando}
                style={btn(P, false)}>💾 Guardar foto</button>
              <button onClick={() => guardar(true)} disabled={guardando}
                style={btn(P, true)}>⭐ Usar como principal</button>
            </div>
            <button onClick={descargar} disabled={guardando}
              style={{ width: '100%', marginTop: '8px', padding: '11px', borderRadius: '9px', border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>
              📥 Descargar imagen
            </button>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
              <button onClick={() => { setResultado(null); }} disabled={guardando}
                style={btnSoft()}>↻ Generar otra</button>
              <button onClick={() => borrar(resultado)} disabled={guardando}
                style={btnSoft('#B91C1C')}>🗑️ Descartar</button>
            </div>
          </div>
        )}

        {/* Selector de estilos + opciones (oculto mientras se ve un resultado) */}
        {!resultado && (
          <>
            <div style={seccion}>
              <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>{fotos.length > 1 ? '2.' : '1.'} ¿Qué tipo de foto quieres crear?</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                {ESTILOS.map((e) => (
                  <button key={e.clave} onClick={() => elegirEstilo(e.clave)}
                    style={{ textAlign: 'left', padding: '11px', borderRadius: '12px', cursor: 'pointer',
                      border: estilo === e.clave ? `2px solid ${P}` : '1px solid #E5E7EB',
                      background: estilo === e.clave ? 'color-mix(in srgb, var(--color-primario,#DB2777) 8%, white)' : '#fff' }}>
                    <div style={{ fontSize: '20px', lineHeight: 1 }}>{e.icono}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', margin: '6px 0 2px' }}>{e.nombre}</div>
                    <div style={{ fontSize: '11.5px', color: '#6B7280', lineHeight: 1.35 }}>{e.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={seccion}>
              <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                {esPersonalizada ? '¿Cómo quieres la foto? *' : 'Instrucciones extra (opcional)'}
              </p>
              <textarea value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)} rows={2} maxLength={500}
                placeholder={esPersonalizada ? 'Ej. sobre una mesa de madera, con fondo tropical…' : 'Ej. fondo más claro, estilo elegante para Instagram…'}
                style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13.5px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />

              <p style={{ margin: '12px 0 6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Formato</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {ASPECTOS.map((a) => (
                  <button key={a.valor} onClick={() => setAspecto(a.valor)}
                    style={{ padding: '6px 11px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                      border: aspecto === a.valor ? `2px solid ${P}` : '1px solid #E5E7EB',
                      background: aspecto === a.valor ? 'color-mix(in srgb, var(--color-primario,#DB2777) 8%, white)' : '#fff',
                      color: aspecto === a.valor ? P : '#6B7280' }}
                    title={a.para}>{a.nombre}</button>
                ))}
              </div>
            </div>

            <div style={{ ...seccion, borderBottom: historial.length ? '1px solid #F3F4F6' : 'none' }}>
              <button onClick={generar} disabled={!puedeGenerar}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', fontSize: '15px', fontWeight: 'bold',
                  color: '#fff', background: puedeGenerar ? P : '#C7C2D6', cursor: puedeGenerar ? 'pointer' : 'not-allowed' }}>
                {generando ? '⏳ Creando tu foto…' : '✨ Crear foto profesional'}
              </button>
              {generando && (
                <p style={{ margin: '10px 0 0', fontSize: '12.5px', color: '#9CA3AF', textAlign: 'center' }}>
                  La IA está trabajando con tu foto. Puede tardar de 1 a 3 minutos cuando hay mucha demanda. No cierres esta ventana…
                </p>
              )}
            </div>
          </>
        )}

        {/* Historial de versiones */}
        {completadas.length > 0 && (
          <div style={seccion}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Versiones anteriores</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {completadas.map((g) => (
                <div key={g.id} style={{ width: '92px' }}>
                  <button onClick={() => setResultado(g)}
                    style={{ padding: 0, border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', width: '92px', height: '92px', background: '#fff' }}>
                    <img src={optimizarFoto(g.resultado_url, 200)} alt="Versión IA" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '3px' }}>
                    <span style={{ fontSize: '10.5px', color: '#9CA3AF' }}>{estiloPorClave(g.estilo)?.nombre?.split(' ')[0] || 'IA'}</span>
                    {confirmarBorrar === g.id ? (
                      <span>
                        <button onClick={() => borrar(g)} style={{ fontSize: '10px', color: '#B91C1C', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Sí</button>
                        <button onClick={() => setConfirmarBorrar(null)} style={{ fontSize: '10px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}>No</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmarBorrar(g.id)} style={{ fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }} title="Eliminar">🗑️</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selector: cuál foto reemplazar (cuando las 5 están llenas) */}
        {reemplazando && (
          <div onClick={() => setReemplazando(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px' }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: '14px', padding: '20px', width: '100%', maxWidth: '420px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#374151' }}>¿Cuál foto quieres reemplazar?</h3>
              <p style={{ margin: '0 0 14px', fontSize: '12.5px', color: '#9CA3AF' }}>
                Ya tienes las 5 fotos llenas. Toca la que quieres cambiar por la nueva — <b>solo se reemplaza esa</b>.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const url = producto[`foto_${n}_url`];
                  if (!url) return null;
                  const esPrincipal = producto.foto_principal === n;
                  return (
                    <button key={n} onClick={() => reemplazarEn(n)} disabled={guardando}
                      style={{ padding: 0, border: esPrincipal ? `2px solid ${P}` : '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', cursor: guardando ? 'wait' : 'pointer', height: '96px', background: '#fff', position: 'relative' }}>
                      <img src={optimizarFoto(url, 200)} alt={`Foto ${n}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {esPrincipal && <span style={{ position: 'absolute', top: '3px', right: '3px', background: P, color: '#fff', fontSize: '9px', padding: '1px 5px', borderRadius: '4px', fontWeight: 700 }}>⭐</span>}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setReemplazando(null)} disabled={guardando}
                style={{ marginTop: '14px', width: '100%', padding: '10px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '9px', fontWeight: 600, cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Figura({ etiqueta, url, destacado, P }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 700, color: destacado ? P : '#9CA3AF', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{etiqueta}</div>
      <div style={{ border: destacado ? `2px solid ${P}` : '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', height: '180px', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={url} alt={etiqueta} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />
      </div>
    </div>
  );
}

const btn = (P, principal) => ({
  padding: '11px', borderRadius: '9px', border: 'none', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer', color: '#fff',
  background: principal ? P : 'var(--k-verde,#0D9488)',
});
const btnSoft = (color = '#374151') => ({
  padding: '10px', borderRadius: '9px', border: '1px solid #E5E7EB', background: '#fff', color, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
});
